import { packsClient } from "lib/clients";
import { useMemo } from "react";
import { useE2EEContext, encryptSessionKey, decryptPackKey } from "lib/e2ee";
import { useXdStorageContext } from "lib/storage";
import useSWR, { SWRConfiguration } from "swr";

const join = async (data: any) => {
  if (data.is_joinable) {
    // Don't interupt if this fails, it is not worth it
    try {
      await packsClient.joinPack(data.id);
    }
    catch (e) {
      console.error(e);
    }
  }
}

const fetcher = async (_key, packId, keys) => {
  const { data } = await packsClient.getPack(packId);

  try {
    data.is_encrypted = !!data?.key_id && !!data?.key;

    if (!data.is_encrypted) {
      data.key_id = null; // If key_id is somehow not set but key is, make sure key is empty and vice versa
      data.key = null;

      await join(data);

      return data;
    }

    const packKey = await decryptPackKey(data, keys);

    if (!packKey) {
      data.key = null;

      return data;
    }

    data.key = packKey;

    const compareKeys = !data.id.startsWith('x') || data.join_key;

    if (compareKeys && data.key_id && keys && data.key_id !== keys.id) {
      return data;
    }

    if (data.is_joinable && keys?.public && !keys.needNonce) {
      // Don't interupt if this fails, it is not worth it
      try {
        const key = await encryptSessionKey(data.key, keys.public);
        await packsClient.joinPack(data.id, key, keys.id);
      }
      catch (e) {
        console.error(e);
      }
    }
  }
  catch (e) {
    console.log(e);
    throw e;
  }

  return data;
}

export const usePack = (packId: string, options?: SWRConfiguration) => {
  const { isLoaded, isEncryptedUser, keys } = useE2EEContext();
  const { isLoaded: isStorageLoaded } = useXdStorageContext();

  const params = useMemo(() => {
    if (!packId) {
      return null;
    }

    if (typeof window !== 'undefined') {
      const hash = window.location.hash?.slice(1);

      if (hash?.length) {
        return [`/v1/pack/${packId}`, packId, keys];
      }
    }

    if (!isLoaded) {
      return null;
    }

    if (isEncryptedUser && (!keys.private || !keys.public)) {
      return null;
    }

    return [`/v1/pack/${packId}`, packId, keys];
  }, [packId, keys, isLoaded, isEncryptedUser]);

  const { data, error, isValidating, mutate } = useSWR(isStorageLoaded ? params : null, fetcher, { revalidateIfStale: false, ...options });

  return {
    data,
    isValidating,
    isLoading: !data && !error,
    error,
    mutate,
    isAnon: data?.anon?.is_valid
  };
};
